import { Link } from "react-router-dom";

const Features = () => {
    const features = [
        {
            title: "Contributor Portal",
            description:
                "Share lessons, devotionals, and creative Bible-based materials that inspire young learners and strengthen their faith.",
            icon: "✍️",
        },
        {
            title: "Teacher Dashboard",
            description:
                "Create engaging classes, manage lessons, and invite learners to participate in interactive Sunday School sessions.",
            icon: "👩‍🏫",
        },
        {
            title: "Parent Access",
            description:
                "Stay connected to your child’s spiritual growth by tracking their lessons, exercises, and progress reports.",
            icon: "👨‍👩‍👧",
        },
        {
            title: "Learner Experience",
            description:
                "Explore engaging Bible stories, watch faith-filled videos, and complete interactive quizzes that help build a strong foundation in Christ.",
            icon: "📖",
        },
        {
            title: "Lesson Management",
            description:
                "Organize Sunday School lessons with structure — from Scripture memory verses to creative activities — all in one place.",
            icon: "🗂️",
        },
        {
            title: "Progress Tracking",
            description:
                "Teachers and parents can monitor each learner’s journey of faith and celebrate every spiritual milestone together.",
            icon: "📈",
        },
        {
            title: "Interactive Exercises",
            description:
                "Reinforce understanding with thought-provoking questions, memory verse challenges, and practical applications.",
            icon: "🧩",
        },
        {
            title: "Class Invitations",
            description:
                "Teachers can invite learners directly to their classes with a simple link or code, making connection easy and organized.",
            icon: "💌",
        },
        {
            title: "Secure & Guided Learning",
            description:
                "A safe, nurturing digital environment where every child can grow in their faith under trusted supervision.",
            icon: "🛡️",
        },
    ];

    const whatWeOffer = [
        {
            title: "📖 Bible-Based Teaching",
            description:
                "Each lesson is grounded in Scripture, helping learners understand God’s truth and apply it in everyday life.",
        },
        {
            title: "👨‍👩‍👧 Faith & Family Connection",
            description:
                "Parents stay involved in their child’s journey of faith through progress tracking, lesson updates, and shared devotion time.",
        },
        {
            title: "👩‍🏫 Teacher-Led Learning",
            description:
                "Dedicated teachers create engaging classes and activities that make Bible learning joyful, interactive, and memorable.",
        },
        {
            title: "✍️ Contributor Resources",
            description:
                "Contributors share creative, Spirit-led content — from devotionals to study guides — helping every class grow together in the Word.",
        },
        {
            title: "🧩 Interactive Learning",
            description:
                "Learners grow through engaging exercises, quizzes, and memory verses that reinforce Biblical truths in fun, practical ways.",
        },
        {
            title: "🙌 Community & Worship",
            description:
                "We encourage fellowship and unity through group worship, prayer, and shared celebrations of faith.",
        },
    ];

    return (
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
            <section className="bg-primary text-white py-20 px-6 text-center">
                <h1 className="text-5xl md:text-6xl font-extrabold mb-4">
                    🌿 Empowering Every Role in Sunday School
                </h1>
                <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8">
                    Our platform brings together contributors, teachers, parents, and learners —
                    creating a connected community that nurtures faith, learning, and growth in Christ.
                </p>
            </section>

            <section className="py-16 px-6 max-w-6xl mx-auto">
                <h2 className="text-4xl font-bold text-gray-800 text-center mb-12">
                    Key Features
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="bg-white shadow-md hover:shadow-xl transition-shadow duration-300 rounded-2xl p-6 flex flex-col items-center text-center"
                        >
                            <div className="text-6xl mb-4">{feature.icon}</div>
                            <h3 className="text-xl font-semibold text-primary mb-2">
                                {feature.title}
                            </h3>
                            <p className="text-gray-700">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-16 px-6 bg-gray-100">
                <div className="max-w-5xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-gray-800 mb-6">
                        🌿 What We Offer
                    </h2>
                    <p className="text-gray-600 text-lg mb-12">
                        Our Sunday School is more than a place of learning — it’s a nurturing
                        community where faith takes root, families grow together, and young
                        hearts are strengthened in the Word of God. We offer Christ-centered
                        experiences designed to teach, guide, and inspire every age group.
                    </p>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 text-left">
                        {whatWeOffer.map((item, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition"
                            >
                                <h3 className="text-xl font-semibold text-primary mb-3">
                                    {item.title}
                                </h3>
                                <p className="text-gray-700">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 text-center">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                    Ready to Build a Stronger Faith Community?
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto mb-8">
                    Join our Sunday School platform today — whether you’re contributing content, teaching classes,
                    guiding your child’s faith journey, or learning God’s Word, we help you grow together in Christ.
                </p>
                <Link to="/signup">
                    <button className="bg-primary text-white font-semibold px-8 py-4 rounded-full hover:bg-blue-600 transition">
                        Get Started Today
                    </button>
                </Link>
            </section>
        </div>
    );
};

export default Features;
